import { HandleType } from '@/enums/handle-type.enum';
import { CAMERA_NODE_DEFINITION_ID } from '@/consts/node-types.consts';

/** Gen Effect's price, the one piece of menu data the server's row can't express. */
const CUSTOM_GLSL_PRICE = 10;
const CUSTOM_GLSL_PAID_PLAN_NOTE = 'Creating Generative Effects is only available on paid plans.';

/** Section placement for the Camera entry, matching the server's `CategoryOrders`. */
const HELPERS_SECTION = { id: 'helpers', order: 40 };

interface MenuNode {
  id?: string;
  order?: number;
  children?: MenuNode[];
}

interface MenuResponseShape {
  menu: { tools?: MenuNode[] };
  recent?: MenuNode[];
}

const removeServerCopies = (menuData: MenuResponseShape, clientItemIds: Set<string>): void => {
  const prune = (items: MenuNode[]): MenuNode[] =>
    items
      .filter((item) => !item.id || !clientItemIds.has(item.id))
      .map((item) => (item.children ? { ...item, children: prune(item.children) } : item));

  menuData.menu.tools = prune(menuData.menu.tools ?? []);
  menuData.recent = prune(menuData.recent ?? []);
};

/**
 * Adds items to a section, reusing the server's if it sent one. Merging rather than pushing a second
 * section is what stops a pruned-empty section being lost and stops the server's being duplicated.
 */
const mergeIntoSection = (
  menuData: MenuResponseShape,
  section: { id: string; order: number },
  items: MenuNode[],
): void => {
  menuData.menu.tools ??= [];
  const existing = menuData.menu.tools.find((candidate) => candidate.id === section.id);
  if (existing) {
    existing.children = [...(existing.children ?? []), ...items];
    return;
  }
  menuData.menu.tools.push({ ...section, children: items });
};

const createCameraPocMenuItem = () => ({
  id: CAMERA_NODE_DEFINITION_ID,
  displayName: 'Camera',
  description: 'Capture photos from the computer camera.',
  icon: 'camera',
  inputTypes: [],
  outputTypes: [HandleType.Image],
  isLeaf: true,
  price: 0,
  searchText: 'camera webcam capture photo',
  isTool: true,
});

/**
 * Keeps Camera hidden unless this app build enables the POC. The backend row has no categories,
 * so this client-owned entry is the only normal discovery path.
 */
export const applyCameraPocMenuEntry = (menuData: MenuResponseShape, enabled: boolean): void => {
  const item = createCameraPocMenuItem();
  removeServerCopies(menuData, new Set([item.id]));
  if (enabled) mergeIntoSection(menuData, HELPERS_SECTION, [item]);
};

/**
 * Gen Effect's price and paid-plan note come from client flags plus the subscription, so the row the
 * server serves can't express them; they're overlaid onto its entry instead. `CustomGlslChargeService`
 * remains the only thing that decides what is actually billed.
 */
export const applyCustomGlslPricing = (
  menuData: MenuResponseShape,
  pricingEnabled: boolean,
  showPaidPlanNote: boolean,
): void => {
  const entries = [
    ...(menuData.menu.tools ?? []).flatMap((category) => category.children ?? []),
    ...(menuData.recent ?? []),
  ];
  for (const entry of entries) {
    if (entry.id === 'custom_glsl') {
      Object.assign(entry, {
        price: pricingEnabled ? CUSTOM_GLSL_PRICE : 0,
        priceDescription: showPaidPlanNote ? CUSTOM_GLSL_PAID_PLAN_NOTE : undefined,
      });
    }
  }
};
